import { PriceBreakdown } from '../api/booking/pricePreviewApiTypes';

/**
 * May this breakdown be saved as a manual price override?
 *
 * The edit dialogs post `manualFinalPrice = breakdown.total` whenever a price
 * adjustment is present. That total is `subtotal − discount`, and the discount
 * field is pre-filled from the *derived* reduction of the booking as it stands
 * (`payment.couponApplied`). The derivation only holds while the base stays
 * put: change the booked dates and the base is re-quoted, while the discount
 * field keeps the figure derived from the old one. The total then goes
 * negative.
 *
 * Both dialogs used to post `Math.max(0, total)` there, which turned that
 * obviously-broken number into a plausible one — the booking was re-billed to
 * **0,00 €** and nothing said so. A Fri–Sun venue booking with a 440 € goodwill
 * adjustment, shortened to a single weekday, priced 110 − 440 = −330 and saved
 * as free.
 *
 * Clamping is the wrong answer twice over: 0,00 € is a legitimate value (an
 * admin comping a booking), so neither the API nor a later reader can tell the
 * artifact from the intent. The only place the two are still distinguishable is
 * here, before the request is built — so this refuses, and the operator fixes
 * the adjustment.
 *
 * Not enforced at the API boundary (ADR-0020) on purpose: the backend cannot
 * reject `manualFinalPrice: 0`, because comping a booking is exactly that
 * request. What is invalid is the *client's* arithmetic reaching it, which only
 * the client can see.
 */
export const priceOverrideIsSaveable = (
    breakdown: Pick<PriceBreakdown, 'total'> | null | undefined,
): boolean => {
    if (!breakdown) return true; // no breakdown → no money field is sent at all
    return Number(breakdown.total) >= 0;
};
